import { EntityId } from "@/shared/types/entity-id";
import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { AirMicrobialBaseData } from "@/services/backend/samples/types/sample-base-data";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesAirMicrobialBaseData = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "SamplesAirMicrobialBaseData" as const, id: request.id },
    { type: "SamplesAirMicrobialBaseData" as const, id: "LIST" },
  ] as const;

export const samplesAirMicrobialBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesAirMicrobialBaseData", "DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      samplesAirMicrobialBaseDataCreate: build.mutation<
        AirMicrobialBaseData,
        CreateRequest
      >({
        query: (request) => ({
          url: "/samples/air-microbial/base-data",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "SamplesAirMicrobialBaseData", id: "LIST" }],
      }),
      samplesAirMicrobialBaseDataShow: build.query<
        AirMicrobialBaseData,
        ShowRequest
      >({
        query: (request) => ({
          url: "/samples/air-microbial/base-data",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("SamplesAirMicrobialBaseData"),
      }),
      /**
       * Shows the base data the company has set as default, the one a new
       * sample is created from. Answers 404 if none is set.
       */
      samplesAirMicrobialBaseDataShowDefault: build.query<
        AirMicrobialBaseData,
        ShowDefaultRequest
      >({
        query: (request) => ({
          url: "/samples/air-microbial/base-data/default",
          method: "GET",
          params: request,
        }),
        providesTags: ["SamplesAirMicrobialBaseData", "DefaultBaseData"],
      }),
      samplesAirMicrobialBaseDataList: build.query<
        Lst<AirMicrobialBaseData>,
        ListRequest
      >({
        query: (request) => ({
          url: "/samples/air-microbial/base-data/list",
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) =>
          providesList(result?.items, "SamplesAirMicrobialBaseData"),
      }),
      samplesAirMicrobialBaseDataPatch: build.mutation<
        AirMicrobialBaseData,
        PatchRequest
      >({
        query: (request) => ({
          url: "/samples/air-microbial/base-data",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesAirMicrobialBaseData,
      }),
      samplesAirMicrobialBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/samples/air-microbial/base-data",
          method: "DELETE",
          body: request,
        }),
        // A deleted instance can no longer be anyone's default, and the
        // backend refuses the delete while it still is — either way the
        // defaults have to be refetched.
        invalidatesTags: [
          { type: "SamplesAirMicrobialBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
    }),
  });

export const {
  useSamplesAirMicrobialBaseDataCreateMutation,
  useSamplesAirMicrobialBaseDataShowQuery,
  useSamplesAirMicrobialBaseDataShowDefaultQuery,
  useLazySamplesAirMicrobialBaseDataShowDefaultQuery,
  useSamplesAirMicrobialBaseDataListQuery,
  useSamplesAirMicrobialBaseDataPatchMutation,
  useSamplesAirMicrobialBaseDataDeleteMutation,
} = samplesAirMicrobialBaseDataApi;

// CreateRequest creates an instance. The label identifies it for the user
// and is unique per company; the defaults it holds are set afterwards
// with a PatchRequest.
export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  label: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ShowDefaultRequest {
  companyId: UUID;
}

export interface ListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  label?: Filter<string>;
  orderBy?: OrdrBy<AirMicrobialBaseDataOrderField>[];
  limit?: number;
  offset?: number;
}

/** The columns the backend can order the base data by. */
export type AirMicrobialBaseDataOrderField =
  | "id"
  | "label"
  | "createdAt"
  | "updatedAt";

// PatchRequest updates the instance. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged (the backend
// `patch.Field` is "unset" when the JSON key is absent). Renaming to a
// label another instance of the company holds is rejected.
export interface PatchRequest {
  id: UUID;
  label?: string;
  unit?: string;
  sampleUnit?: string;
  samplingVolume?: number;
  dg18?: boolean;
  /** The default lab; null removes it. */
  labId?: EntityId;
  /** The default device; null removes it. */
  deviceId?: EntityId;
}

export interface DeleteRequest {
  id: UUID;
}

/**
 * Patches the cached instance for an optimistic update. Defined here, at
 * module scope, so it keeps one identity for the whole app — see
 * {@link updateCache}.
 */
export const updateSamplesAirMicrobialBaseDataCache = updateCache(
  samplesAirMicrobialBaseDataApi,
  "samplesAirMicrobialBaseDataShow",
);
